import { parseDateTz } from "@mgdx/shared/src/utils/parseDateTz";
import { tzDate } from "@mgdx-libs/dayjs";
import includes from "lodash/includes";
import { Fragment, useCallback, useEffect } from "react";
import { InView } from "react-intersection-observer";

import { SenderTypeEnum, TalkMessageDocumentWithId } from "../../models/TalkMessageDocument";
import { TalkRoomDocument } from "../../models/TalkRoomDocument";
import { TalkGuide } from "../TalkGuide";
import { MessageTypeEnum, TalkMessage } from "../TalkMessage";

export type OnVisibleUnreadMessage = (messageId: string) => void;

export type TalkContentProps = {
  room?: TalkRoomDocument;
  messages?: TalkMessageDocumentWithId[];
  talkSenderType: SenderTypeEnum;
  onVisibleUnreadMessage: OnVisibleUnreadMessage;
  containment: HTMLDivElement | null;
  onDidMount: () => void;
};

export const TalkContent = ({
  room,
  messages,
  talkSenderType,
  onVisibleUnreadMessage,
  containment,
  onDidMount,
}: TalkContentProps) => {
  const getIsSameDate = useCallback(
    (message: TalkMessageDocumentWithId, index: number) => {
      const prevMessage = messages && messages[index - 1];

      if (!prevMessage) return false;

      const prevSentDate = parseDateTz(prevMessage.sentAt);
      if (!prevSentDate) return false;

      const sentDate = parseDateTz(message.sentAt);
      if (!sentDate) return false;

      return prevSentDate.isSame(sentDate, "date");
    },
    [messages]
  );

  const getDateGuide = useCallback(
    (message: TalkMessageDocumentWithId) => {
      const sentDate = parseDateTz(message.sentAt);
      if (!sentDate) return "";

      if (sentDate.isSame(tzDate().toDate(), "date")) {
        return "本日";
      }

      if (sentDate.isSame(tzDate().add(-1, "day").toDate())) {
        return "昨日";
      }

      const isPatient = talkSenderType === SenderTypeEnum.Patient;

      if (sentDate.isAfter(tzDate().add(-7, "day").toDate())) {
        return sentDate.format(isPatient ? "ddd曜" : "ddd");
      }

      if (sentDate.isSame(tzDate().toDate(), "years")) {
        return sentDate.format(isPatient ? "MM月DD日 (ddd曜)" : "MM月DD日 (ddd)");
      }

      return sentDate.format(isPatient ? "YYYY年MM月DD日 (ddd曜)" : "YYYY年MM月DD日 (ddd)");
    },
    [talkSenderType]
  );

  const getMessageType = useCallback(
    (message: TalkMessageDocumentWithId) => {
      if (talkSenderType === message.senderType) {
        return MessageTypeEnum.Sent;
      }

      return MessageTypeEnum.Received;
    },
    [talkSenderType]
  );

  const getIsFirstMessage = useCallback(
    (message: TalkMessageDocumentWithId, index: number) => {
      const prevMessage = messages && messages[index - 1];

      if (!prevMessage) return true;
      if (prevMessage.senderType !== message.senderType) return true;

      const prevSentDate = parseDateTz(prevMessage.sentAt);
      if (!prevSentDate) return true;

      const sentDate = parseDateTz(message.sentAt);
      if (!sentDate) return true;

      return sentDate.diff(prevSentDate, "seconds") > 60;
    },
    [messages]
  );

  const getIsLastMessage = useCallback(
    (message: TalkMessageDocumentWithId, index: number) => {
      const nextMessage = messages && messages[index + 1];

      if (!nextMessage) return true;
      if (nextMessage.senderType !== message.senderType) return true;

      const sentDate = parseDateTz(message.sentAt);
      if (!sentDate) return true;

      const nextSentDate = parseDateTz(nextMessage.sentAt);
      if (!nextSentDate) return true;

      return nextSentDate.diff(sentDate, "seconds") > 60;
    },
    [messages]
  );

  const getIsOtherSideUnread = useCallback(
    (message: TalkMessageDocumentWithId) => {
      if (talkSenderType === SenderTypeEnum.Patient) {
        return includes(room?.medicalUnreadMessageIds, message.id);
      }

      return includes(room?.patientUnreadMessageIds, message.id);
    },
    [talkSenderType, room]
  );

  const getIsSenderUnread = useCallback(
    (message: TalkMessageDocumentWithId) => {
      if (talkSenderType === SenderTypeEnum.Patient) {
        return includes(room?.patientUnreadMessageIds, message.id);
      }

      return includes(room?.medicalUnreadMessageIds, message.id);
    },
    [talkSenderType, room]
  );

  useEffect(() => {
    if (onDidMount) {
      onDidMount();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!room) {
    return null;
  }

  if (!messages) {
    return null;
  }

  if (messages.length === 0) {
    return <TalkGuide message="トークルームが作成されました" />;
  }

  return (
    <Fragment>
      {messages.map((message, index) => {
        const isSameDate = getIsSameDate(message, index);
        const messageType = getMessageType(message);
        const isOtherSideUnread = getIsOtherSideUnread(message);
        const isSenderUnread = getIsSenderUnread(message);
        const isFirstMessage = getIsFirstMessage(message, index);
        const isLastMessage = getIsLastMessage(message, index);
        const handleVisibleChange = (isVisible: boolean) => {
          if (!isSenderUnread) return;
          if (!isVisible) return;

          onVisibleUnreadMessage(message.id);
        };

        return (
          <InView onChange={handleVisibleChange} key={message.id} triggerOnce={!isSenderUnread} root={containment}>
            {!isSameDate && <TalkGuide message={getDateGuide(message)} />}
            <TalkMessage
              message={message}
              messageType={messageType}
              isFirstMessage={isFirstMessage}
              isLastMessage={isLastMessage}
              isUnread={isOtherSideUnread}
            />
          </InView>
        );
      })}
    </Fragment>
  );
};
